import React, { useState, Fragment } from 'react';
import { motion } from 'framer-motion';
import { tools, matrixData, FeatureStatus } from '../data/tools';
import { ToolLogo } from './Logos';
const StatusBlock = ({
  status
}: {
  status: FeatureStatus;
}) => {
  switch (status) {
    case 'full':
      return <div className="w-4 h-4 bg-lime mx-auto shadow-[0_0_10px_rgba(196,240,66,0.5)]" data-id="element-71" />;
    case 'partial':
      return <div className="w-4 h-4 border-2 border-amber-500 mx-auto" data-id="element-72" />;
    case 'none':
      return <div className="w-4 h-1 bg-ink/20 mx-auto" data-id="element-73" />;
  }
};
export function ComparisonMatrix() {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const filteredData = activeCategory === 'All' ? matrixData : matrixData.filter(c => c.name === activeCategory);
  return <section id="compare" className="py-24 bg-base relative" data-id="element-74">
      <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:pl-24 lg:pr-8" data-id="element-75">
        <div className="mb-12 font-mono" data-id="element-76">
          <div className="text-lime text-xs tracking-widest mb-4" data-id="element-77">
            §03 / SIGNAL BOARD
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ink uppercase tracking-tight mb-6" data-id="element-78">
            Telemetry Matrix
          </h2>

          <div className="flex flex-wrap items-center gap-2 mb-8 text-xs" data-id="element-79">
            <button onClick={() => setActiveCategory('All')} className={`px-4 py-2 hairline-all transition-colors ${activeCategory === 'All' ? 'bg-ink text-base font-bold' : 'bg-base text-ink/60 hover:bg-ink/10'}`} data-id="element-80">
              [ ALL_SIGNALS ]
            </button>
            {matrixData.map(cat => <button key={cat.name} onClick={() => setActiveCategory(cat.name)} className={`px-4 py-2 hairline-all transition-colors uppercase ${activeCategory === cat.name ? 'bg-ink text-base font-bold' : 'bg-base text-ink/60 hover:bg-ink/10'}`} data-id="element-81">
                [ {cat.name.replace(/ & /g, '_')} ]
              </button>)}
          </div>

          <div className="flex items-center gap-6 text-[10px] tracking-widest text-ink/60 hairline-all inline-flex px-4 py-2 bg-ink/5" data-id="element-82">
            <div className="flex items-center gap-2" data-id="element-83">
              <StatusBlock status="full" data-id="element-84" /> FULL
            </div>
            <div className="flex items-center gap-2" data-id="element-85">
              <StatusBlock status="partial" data-id="element-86" /> PARTIAL
            </div>
            <div className="flex items-center gap-2" data-id="element-87">
              <StatusBlock status="none" data-id="element-88" /> NONE
            </div>
          </div>
        </div>

        <div className="relative hairline-all bg-base overflow-hidden" data-id="element-89">
          <div className="overflow-x-auto" data-id="element-90">
            <table className="w-full text-left border-collapse min-w-[1000px] font-mono text-xs" data-id="element-91">
              <thead data-id="element-92">
                <tr data-id="element-93">
                  <th className="sticky left-0 top-0 z-30 bg-base hairline-b hairline-r p-4 w-64" data-id="element-94">
                    <span className="text-[10px] text-ink/40 tracking-widest" data-id="element-95">
                      CAPABILITY_ID
                    </span>
                  </th>
                  {tools.map(tool => {
                  const isWaydev = tool.id === 'waydev';
                  return <th key={tool.id} className={`sticky top-0 z-20 hairline-b p-4 min-w-[120px] text-center ${isWaydev ? 'bg-lime/10 border-b-lime' : 'bg-base'}`} data-id="element-96">
                        {isWaydev && <div className="absolute top-0 left-0 right-0 h-1 bg-lime" data-id="element-97" />}
                        <div className="flex flex-col items-center gap-3" data-id="element-98">
                          <ToolLogo toolId={tool.id} className={`w-6 h-6 ${isWaydev ? 'stroke-lime' : ''}`} data-id="element-99" />
                          <span className={`font-bold tracking-wider ${isWaydev ? 'text-lime' : 'text-ink'}`} data-id="element-100">
                            {tool.name.toUpperCase()}
                          </span>
                        </div>
                      </th>;
                })}
                </tr>
              </thead>
              <tbody data-id="element-101">
                {filteredData.map(category => <Fragment key={category.name} data-id="element-102">
                    <tr data-id="element-103">
                      <td colSpan={tools.length + 1} className="bg-ink/5 hairline-b p-2 sticky left-0 z-10" data-id="element-104">
                        <span className="text-[10px] font-bold text-indigo-400 tracking-widest ml-2" data-id="element-105">
                          // {category.name.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                    {category.features.map((feature, fIdx) => <motion.tr key={feature.id} initial={{
                  opacity: 0
                }} whileInView={{
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.2,
                  delay: fIdx * 0.05
                }} className="hover:bg-ink/5 transition-colors group" data-id="element-106">
                        <td className="sticky left-0 z-10 bg-base hairline-b hairline-r p-4 group-hover:bg-[#111218] transition-colors" data-id="element-107">
                          <span className="text-ink/80" data-id="element-108">
                            {feature.name.toUpperCase()}
                          </span>
                        </td>
                        {tools.map(tool => {
                    const isWaydev = tool.id === 'waydev';
                    return <td key={`${tool.id}-${feature.id}`} className={`hairline-b p-4 text-center ${isWaydev ? 'bg-lime/5' : ''}`} data-id="element-109">
                              <StatusBlock status={feature.values[tool.id]} data-id="element-110" />
                            </td>;
                  })}
                      </motion.tr>)}
                  </Fragment>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>;
}